import mainIcon from "@assets/images/main-icon.png";
import ClockSettingsIcon from "@assets/icons/clock-settings.svg";

import useDialogStore from "@store/useDialogStore";
import useUserStore from "@store/useUserStore";
import { MAINTENANCE_STRINGS } from "@config/messages";
import { resolveUserTimeZone } from "@utils/date.util";
import { formatWindowParts, getTimeZoneAbbreviation } from "@utils/maintenance.util";
import type { MaintenanceWindow } from "@app-types/maintenance.type";
import { BaseDialog } from "@ui/Dialogs";
import { Paragraph } from "@ui/Content";

interface Props {
  window: MaintenanceWindow;
}

export function MaintenanceAlertModal({ window }: Props) {
  const { closeDialog } = useDialogStore();
  const { activeUser } = useUserStore();

  const timeZone = resolveUserTimeZone(activeUser?.timezone);
  const abbreviation = getTimeZoneAbbreviation(timeZone, window.startsAt);
  const { date, start, end } = formatWindowParts(window, timeZone);

  return (
    <BaseDialog
      title={MAINTENANCE_STRINGS.ALERT_TITLE}
      titleClassName="text-lg"
      HeaderPrefixIcon={<img src={mainIcon} className="h-6 w-6 flex-shrink-0" alt="Meridian" />}
      bodyClassName="px-4 pb-1"
      showCloseButton
      primaryButton={{ label: MAINTENANCE_STRINGS.ALERT_CTA, callback: closeDialog }}
    >
      <Paragraph className="!text-left">{MAINTENANCE_STRINGS.ALERT_BODY}</Paragraph>

      {/* Figma tokens: bg #fbf7f4, label/icon #a16207 (yellow-700). */}
      <div className="mt-4 flex items-center gap-2 rounded-lg bg-[#fbf7f4] px-3 py-2.5 dark:bg-gray-700">
        <ClockSettingsIcon className="h-5 w-5 flex-shrink-0 text-yellow-700 dark:text-yellow-500" />
        <div className="min-w-0 text-sm">
          {/* A day shared by both endpoints rides along with the label, keeping the times together. */}
          <p className="font-medium text-yellow-700 dark:text-yellow-500">
            {MAINTENANCE_STRINGS.ALERT_BANNER_LABEL}
            {date ? (
              <span className="whitespace-nowrap font-normal text-content-primary dark:text-gray-100">
                {" "}
                {date},
              </span>
            ) : null}
          </p>
          {/* Neither endpoint may break internally; the end drops to its own line when needed. */}
          <p className="text-content-primary dark:text-gray-100">
            <span className="whitespace-nowrap">{start} –</span>{" "}
            <span className="whitespace-nowrap">
              {end}
              {abbreviation ? ` ${abbreviation}` : ""}
            </span>
          </p>
        </div>
      </div>
    </BaseDialog>
  );
}
